
import React, { useState, useEffect } from 'react';
import { Grid, TextField, Button, Paper, Typography, Box, CircularProgress, Alert, IconButton, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { createLoan, updateLoan, getLoanById, listCompanies } from '../../api';
import img from '../../assets/banner.jpg';

const LoanForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!(location.state?.data || id);

  const tenureOptions = [
    { value: 'monthly', label: 'Monthly', months: 1 },
    { value: 'quarterly', label: 'Quarterly', months: 3 },
    { value: 'halfyearly', label: 'Half-Yearly', months: 6 },
    { value: 'yearly', label: 'Yearly', months: 12 }
  ];

  const interestTypeOptions = [
    { value: 'fixed', label: 'Fixed' },
    { value: 'reducing', label: 'Reducing' }
  ];

  const [formData, setFormData] = useState({
    company_name: '',
    covered_entity: '',
    interest_type: 'fixed',
    locations: [{ covered_location: '' }],
    tenure_frequency: 'monthly',
    start_date: '',
    maturity_date: '',
    emi_date: '',
    principal_amount: '',
    interest_rate: '',
    interest_prior_month: '',
    interest_paid: '',
    emi_amount: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        // Fetch companies for dropdown
        const companiesData = await listCompanies();
        setCompanies(Array.isArray(companiesData) ? companiesData : []);

        // Load existing loan data if in edit mode
        const initial = location.state?.data;
        if (initial) {
          setFormData({
            company_name: initial.company_name || '',
            covered_entity: '',
            interest_type: initial.interest_type || 'fixed',
            locations: initial.locations || [{ covered_location: initial.location || '' }],
            tenure_frequency: initial.tenure_frequency || 'monthly',
            start_date: initial.start_date ? initial.start_date.split('T')[0] : '',
            maturity_date: initial.maturity_date ? initial.maturity_date.split('T')[0] : '',
            emi_date: initial.emi_date ? initial.emi_date.split('T')[0] : '',
            principal_amount: initial.principal_amount || '',
            interest_rate: initial.interest_rate || '',
            interest_prior_month: initial.interest_prior_month || '',
            interest_paid: initial.interest_paid || '',
            emi_amount: initial.emi_amount || ''
          });
        } else if (id) {
          const fetched = await getLoanById(id);
          setFormData({
            company_name: fetched.company_name || '',
            covered_entity: '',
            interest_type: fetched.interest_type || 'fixed',
            locations: fetched.locations || [{ covered_location: fetched.location || '' }],
            tenure_frequency: fetched.tenure_frequency || 'monthly',
            start_date: fetched.start_date ? fetched.start_date.split('T')[0] : '',
            maturity_date: fetched.maturity_date ? fetched.maturity_date.split('T')[0] : '',
            emi_date: fetched.emi_date ? fetched.emi_date.split('T')[0] : '',
            principal_amount: fetched.principal_amount || '',
            interest_rate: fetched.interest_rate || '',
            interest_prior_month: fetched.interest_prior_month || '',
            interest_paid: fetched.interest_paid || '',
            emi_amount: fetched.emi_amount || ''
          });
        }
      } catch (err) {
        setError('Failed to load loan data or companies');
        console.error('Error loading data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadFormData();
  }, [id, location.state]);

  const handleChange = (e, index = null, fieldType = null) => {
    const { name, value } = e.target;
    if (fieldType === 'covered_location') {
      const updatedLocations = [...formData.locations];
      updatedLocations[index][fieldType] = value;
      setFormData({ ...formData, locations: updatedLocations });
    } else {
      const updatedFormData = { ...formData, [name]: value };

      // Auto-calculate Interest Paid and EMI Amount
      const principal = parseFloat(updatedFormData.principal_amount) || 0;
      const rate = parseFloat(updatedFormData.interest_rate) || 0;
      const tenureOption = tenureOptions.find(opt => opt.value === updatedFormData.tenure_frequency);
      const months = tenureOption ? tenureOption.months : 1;

      // Interest Paid = (Principal Amount * Interest Rate / 100) / Tenure Frequency in months
      updatedFormData.interest_paid = ((principal * rate / 100) / months).toFixed(2);

      // EMI Amount calculation: EMI = [P * r * (1 + r)^n] / [(1 + r)^n - 1]
      const monthlyRate = rate / 100 / 12;
      const tenureInMonths = months * 12; // Assuming tenure is in years for EMI calculation
      if (principal && monthlyRate && tenureInMonths) {
        const power = Math.pow(1 + monthlyRate, tenureInMonths);
        updatedFormData.emi_amount = (principal * monthlyRate * power / (power - 1)).toFixed(2);
      } else {
        updatedFormData.emi_amount = '';
      }

      setFormData(updatedFormData);
    }
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { covered_location: '' }]
    });
  };

  const deleteLocation = (index) => {
    if (formData.locations.length === 1) return; // Prevent deleting the last location
    const updatedLocations = formData.locations.filter((_, i) => i !== index);
    setFormData({ ...formData, locations: updatedLocations });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await updateLoan(id || formData.id, formData);
      } else {
        await createLoan(formData);
      }
      navigate('/view-all', {
        state: { message: isEdit ? 'Loan updated successfully!' : 'Loan created successfully!' }
      });
    } catch (err) {
      setError(err.message || 'Submission failed');
      console.error('Error submitting loan form:', err);
    } finally {
      setLoading(false);
    }
  };

  const isDateField = (type) => type === 'date';

  if (loadingData) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `url(${img})`,
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        py: 5,
        px: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 9,
          borderRadius: 3,
          backgroundColor: '#121212',
          color: 'text.primary',
          maxWidth: 1200,
          mx: 'auto',
          boxShadow: 3
        }}
      >
        <Typography variant="h6" align="center" gutterBottom>
          {isEdit ? 'Edit Loan' : 'Loan Payment Details'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} direction="column">
            {/* First Row: Company Name */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" style={{marginRight:'185px'}}>
                  <InputLabel id="company-name-label" sx={{ color: '#fdfcfbff' }}>
                    Company Name
                  </InputLabel>
                  <Select
                    labelId="company-name-label"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      color: '#fff',
                      backgroundColor: '#1e1e1e',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a company</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company._id} value={company.company_name}>
                        {company.company_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Second Row: Covered Entity, Interest Type */}
            <Grid container item spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Covered Entity"
                  name="covered_entity"
                  value={formData.covered_entity}
                  onChange={handleChange}
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                    }
                  }}
                  InputProps={{ sx: { py: 1 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#ccc' },
                    '&:hover fieldset': { borderColor: '#e0b256' },
                    '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                  }
                }}>
                  <InputLabel sx={{ 
                    '&.Mui-focused': { color: '#e0b256' },
                    fontSize: '1.2rem'
                  }}>Interest Type</InputLabel>
                  <Select
                    value={formData.interest_type}
                    name="interest_type"
                    onChange={handleChange}
                    label="Interest Type"
                    sx={{
                      fontSize: '1.2rem',
                      '& .MuiSelect-select': { py: 1.5 }
                    }}
                  >
                    {interestTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.2rem' }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Third Row: Covered Location with Plus and Delete Buttons */}
            {formData.locations.map((location, index) => (
              <Grid container item spacing={4} key={index}>
                <Grid item xs={12} sm={formData.locations.length > 1 ? 10.5 : 11}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Covered Location"
                    name={`covered_location_${index}`}
                    value={location.covered_location}
                    onChange={(e) => handleChange(e, index, 'covered_location')}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  {index === 0 ? (
                    <IconButton onClick={addLocation} sx={{ color: '#e0b256' }}>
                      <AddCircleIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => deleteLocation(index)} sx={{ color: '#e0b256' }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}

            {/* Fourth Row: Tenure Frequency, Start Date, Maturity Date, EMI Date */}
            <Grid container item spacing={4}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small" sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#ccc' },
                    '&:hover fieldset': { borderColor: '#e0b256' },
                    '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                  }
                }}>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#e0b256' } }}>Tenure Frequency</InputLabel>
                  <Select
                    value={formData.tenure_frequency}
                    name="tenure_frequency"
                    onChange={handleChange}
                    label="Tenure Frequency"
                  >
                    {tenureOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {[
                { label: 'Start Date', name: 'start_date', type: 'date' },
                { label: 'Maturity Date', name: 'maturity_date', type: 'date' },
                { label: 'EMI Date', name: 'emi_date', type: 'date' }
              ].map((field) => (
                <Grid item xs={12} sm={3} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    inputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Fifth Row: Principal Amount, Interest Rate, Interest Prior Month, Interest Paid */}
            <Grid container item spacing={4}>
              {[
                { label: 'Principal Amount', name: 'principal_amount', type: 'number' },
                { label: 'Interest Rate (%)', name: 'interest_rate', type: 'number' },
                { label: 'Interest Prior Month', name: 'interest_prior_month', type: 'number' },
                { label: 'Interest Paid', name: 'interest_paid', type: 'number', disabled: true }
              ].map((field) => (
                <Grid item xs={12} sm={3} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={handleChange}
                    disabled={field.disabled || false}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Sixth Row: EMI Amount */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="EMI Amount"
                  name="emi_amount"
                  type="number"
                  value={formData.emi_amount}
                  disabled
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                    }
                  }}
                  InputProps={{ sx: { py: 1 } }}
                />
              </Grid>
            </Grid>

            {/* Submit and Cancel Buttons */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    mt: 1.5,
                    fontWeight: 'bold',
                    backgroundColor: '#e0b256',
                    color: '#000',
                    '&:hover': { backgroundColor: '#c99e3c' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : isEdit ? 'Update' : 'Submit'}
                </Button>
              </Grid>
              {isEdit && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1.25,
                      mt: 1,
                      fontWeight: 'bold',
                      color: '#e0b256',
                      borderColor: '#e0b256',
                      '&:hover': { backgroundColor: '#222' }
                    }}
                    onClick={() => navigate('/view-all')}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default LoanForm;